import { create } from 'zustand';
import api from '../services/axiosInstance'
import data_api from '../services/axiosData';

const PASS = '07callatm'

const useSiniestrosStore = create((set) => ({
    config: {},
    loading: false,
    error: null,
    claimInstance: {},
    claimToken: null,
    step: null,
    sinisterData: [],
    injures: [],
    claim_number: null,

    /**
     * Clears the current claim instance from the store.
     * 
     * Sets the `claimInstance` state to an empty object.
     */
    clearInstance: () => {
        set({ claimInstance: {} });
    },


    /**
     * Retrieves the configuration from the API and sets the `config` state with the response.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     * 
     * @returns {Promise<boolean>} true if the API request was successful, false otherwise.
     */
    getConfig: async () => {
        set({ error: null });
        try {
            const res = await api.get('/config');
            set({ config: res.data, loading: false });

            return true
        } catch (err) {
            set({ config: { app_status_active: false }, loading: false });
            console.error(err)
        }
    },


    /**
     * Retrieves the models by branch and year from the API and sets the `modelos` state with the response.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     * 
     * @param {string} rama - The branch of the models to retrieve.
     * @param {number} anio - The year of the models to retrieve.
     * 
     * @returns {Promise<Object[]>} The response from the API call, which is an array of objects containing the models information.
     */
    getMarcas: async (rama, anio) => {
        set({ error: null });
        try {
            const res = await data_api.get('/params/marcas_por_rama_y_año', {
                params: { rama, anio }
            });
            set({ marcas: res.data, loading: false });
            return res.data;
        } catch (err) {
            set({ error: err.message || 'Error al cargar marcas', loading: false });
            console.error(err);
        }
    },

    getModelos: async (marca, anio) => {
        set({ error: null });
        try {
            const res = await data_api.get('/params/modelos_por_marca_y_año', {
                params: { marca, anio }
            });
            set({ modelos: res.data, loading: false });
            return res.data;
        } catch (err) {
            set({ error: err.message || 'Error al cargar modelos', loading: false });
            console.error(err);
        }
    },


    /**
     * Retrieves a claim instance by its UUID from the API and sets the `claimInstance` state with the response.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @returns {Promise<Object>} The claim instance data from the API response if successful.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    getInstance: async (uuid) => {
        set({ loading: true, error: null });
        try {
            const res = await api.get(`/instance/${uuid}`);
            set({ claimInstance: res.data, loading: false });
            return res.data
        } catch (err) {
            set({ error: err.message || 'Error al cargar instancia', loading: false });
            console.error(err)
        }
    },

    /**
     * Creates a new claim instance, and sets the claim token in the state.
     * 
     * @returns {Promise<string>} The claim token if the API request was successful, an error object otherwise.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    createClaim: async () => {
        set({ loading: true, error: null });
        try {
            const res = await api.post('/instance/create');
            set({ claimToken: res.data.uuid, loading: false });
            return res.data.uuid
        } catch (err) {
            set({ error: err.message || 'Error al crear reclamo', loading: false });
            console.error(err)
        }
    },


    /**
     * Uploads files associated with the claim instance with the given UUID.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @param {Object} data - The data containing files to be uploaded, with keys as field names and values as file objects.
     * @returns {Promise<boolean>} true if the API request was successful, false otherwise.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    uploadFiles: async (uuid, data) => {
        set({ loading: true, error: null });
        try {
            const formData = new FormData();

            for (const [key, value] of Object.entries(data)) {
                if (value) {
                    formData.append(key, value);
                }
            }

            const res = await api.post(`instance/${uuid}/update`, formData, {
                headers: {
                    'Accept': 'multipart/form-data',
                    'Content-Type': undefined
                }
            });

            set({ claimInstance: res.data, loading: false });

            return true;
        } catch (err) {
            set({ error: err.message || 'Error al cargar archivos', loading: false });
            console.error(err);
            return false;
        }
    },

    /**
     * Updates the claim instance with new data.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @param {Object} data - The data to update the claim instance with.
     * @returns {Promise<Object>} The response from the API call.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and returns an error object.
     */
    updateClaim: async (uuid, data) => {
        set({ loading: true, error: null });
        try {
            const res = await api.post(`instance/${uuid}/update`, data);
            set({ loading: false });
            return res;
        } catch (err) {
            set({ error: err.message || 'Error al actualizar claim', loading: false });
            console.error(err);
            throw err
        }
    },

    /**
     * Validates sinister data by making an API call with the provided data and UUID.
     *
     * @param {Object} data - The data to be used in the validation process.
     * @param {string} uuid - The unique identifier associated with the sinister data.
     * @returns {Promise<Object>} The validated sinister data from the API response if successful.
     *
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    validateSinister: async (data, uuid) => {
        set({ loading: true, error: null });
        try {
            const res = await api.get('/validations/sinister', {
                params: {
                    ...data,
                    uuid: uuid
                }
            });
            set({ sinisterData: res.data[0], loading: false });

            return res
        } catch (err) {
            set({ error: err.message || 'Error al validar cliente', loading: false });
            console.error("validateInsured error:", err);
            throw err;
        }
    },


    /**
     * Validates insured data by making an API call with the provided data and UUID.
     * 
     * @param {Object} data - The data to be used in the validation process.
     * @param {string} uuid - The unique identifier associated with the insured data.
     * @returns {Promise<Object>} The validated insured data from the API response if successful.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    validateInsured: async (data, uuid) => {
        set({ loading: true, error: null });
        try {
            const res = await api.get(`/validations/insured`, {
                params: {
                    ...data,
                    uuid: uuid
                }
            });
            set({ sinisterData: res.data, loading: false });
            return res;
        } catch (err) {
            set({ error: err.message || 'Error al validar cliente', loading: false });
            console.error("validateInsured error:", err);
            throw err;
        }
    },

    /**
     * Creates a new injured person associated with the claim instance with the given UUID.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @param {Object} data - The data to be used to create the injured person.
     * @returns {Promise<boolean>} true if the API request was successful, false otherwise.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    createInjured: async (uuid, data) => {
        set({ loading: true, error: null });
        try {
            const formData = new FormData();

            for (const [key, value] of Object.entries(data)) {
                if (value) {
                    formData.append(key, value);
                }
            }

            const res = await api.post(`/instance/${uuid}/injured/create`, formData, {
                headers: {
                    'Accept': 'multipart/form-data',
                    'Content-Type': undefined
                }
            });
            set({ injures: res.data, loading: false });
            return true;
        } catch (err) {
            set({ error: err.message || 'Error al crear lesionado', loading: false });
            console.error(err);
        }
    },

    /**
     * Deletes an injured person associated with the claim instance with the given UUID.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @param {string} injuredUuid - The unique identifier of the injured person to delete.
     * @returns {Promise<boolean>} true if the API request was successful, false otherwise.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    deleteInjured: async (uuid, injuredUuid) => {
        set({ loading: true, error: null });
        try {
            const res = await api.get(`/instance/${uuid}/injured/${injuredUuid}/delete`);
            set({ loading: false });
            return true;
        } catch (err) {
            set({ error: err.message || 'Error al eliminar lesionado', loading: false });
            console.error(err);
        }
    },

    /**
     * Validates the claim instance with the given UUID.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @returns {Promise<Object>} The response from the API call.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    validateClaim: async (uuid) => {
        set({ loading: true, error: null });
        try {
            const res = await api.get(`/instance/${uuid}/validate`);
            set({ loading: false });
            return res;
        } catch (err) {
            set({ error: err.message || 'Error al validar reclamo', loading: false });
            console.error(err)
            return err;
        }
    },

    /**
     * Submits the claim instance with the given UUID.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @returns {Promise<string>} The claim number if the API request was successful, an error object otherwise.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    submitClaim: async (uuid) => {
        set({ loading: true, error: null });
        try {
            const res = await api.get(`/instance/${uuid}/submit`);
            set({ claim_number: res.data, loading: false });
            return res.data;
        } catch (err) {
            set({ error: err.message || 'Error al enviar reclamo', loading: false });
            console.error(err)
            return err;
        }
    },

    /**
     * Obtiene el estado de un siniestro.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @returns {Promise<string>} The claim number if the API request was successful, an error object otherwise.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    getSinisterStatus: async (uuid) => {
        set({ loading: true, error: null });
        try {
            const res = await api.get(`/sinister-status/${uuid}`);
            set({ loading: false });
            return res.data;
        } catch (err) {
            set({ error: err.message || 'Error al obtener el estado del siniestro', loading: false });
            console.error(err)
            return err;
        }
    },

    /**
     * Obtiene el estado de un siniestro.
     * 
     * @param {string} uuid - The unique identifier of the claim instance.
     * @returns {Promise<string>} The claim number if the API request was successful, an error object otherwise.
     * 
     * Sets loading state to true while the API request is in progress and to false once it completes.
     * If an error occurs, sets the error state with the error message and logs the error to the console.
     */
    submitTicket: async (uuid, uuidTopic, question) => {
        set({ loading: true, error: null });
        try {
            const res = await api.post(`/sinister-status/${uuid}/topic/${uuidTopic}`, question);
            set({ loading: false });
            return res;
        } catch (err) {
            set({ error: err.message || 'Error al subir una pregunta', loading: false });
            console.error(err)
            return err;
        }
    }
}));

export default useSiniestrosStore;
